import React from "react";

/**
 * Global Error Boundary — catches any JS render error in the React tree.
 * Without it, an uncaught render error (React 18+) unmounts the whole app and leaves a white screen.
 * With it, the user sees a friendly fallback and a "Reload" button.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // ─── Auto-recovery: ChunkLoadError happens when a stale index.html (served by an old
    // service worker) references a JS chunk that no longer exists on the server after a redeploy.
    // We unregister the SW + clear caches + hard reload ONCE (sessionStorage flag prevents loops).
    const msg = String(error?.message || error || "");
    const isChunkError = /ChunkLoadError|Loading chunk \d+ failed|Failed to fetch dynamically imported|Importing a module script failed/i.test(msg);
    if (isChunkError && !sessionStorage.getItem("__hostland_chunk_recovery__")) {
      sessionStorage.setItem("__hostland_chunk_recovery__", "1");
      (async () => {
        try {
          if ("serviceWorker" in navigator) {
            const regs = await navigator.serviceWorker.getRegistrations();
            await Promise.all(regs.map((r) => r.unregister()));
          }
          if ("caches" in window) {
            const keys = await caches.keys();
            await Promise.all(keys.map((k) => caches.delete(k)));
          }
        } catch {/* ignore */}
        // Bypass HTTP cache: append a cache-bust query param
        const url = new URL(window.location.href);
        url.searchParams.set("__r", Date.now().toString(36));
        window.location.replace(url.toString());
      })();
      return;
    }

    // Lightweight remote logging — best-effort, no PII
    try {
      const payload = {
        message: msg,
        stack: String(error?.stack || "").slice(0, 1200),
        component_stack: String(info?.componentStack || "").slice(0, 1200),
        url: typeof window !== "undefined" ? window.location.href : "",
        ts: new Date().toISOString(),
      };
      if (typeof navigator !== "undefined" && navigator.sendBeacon) {
        const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
        navigator.sendBeacon(`${process.env.REACT_APP_BACKEND_URL}/api/client-errors`, blob);
      }
    } catch {
      /* swallow */
    }
    // eslint-disable-next-line no-console
    console.error("ErrorBoundary caught:", error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "32px",
          textAlign: "center",
          background: "linear-gradient(135deg,#FAFAFA,#FFFFFF)",
          fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        }}>
          <div style={{
            fontSize: 64, marginBottom: 12, lineHeight: 1,
          }}>⚠️</div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: "#18181B", margin: "0 0 8px", letterSpacing: "-0.02em" }}>
            Qualcosa è andato storto
          </h1>
          <p style={{ fontSize: 14, color: "#71717A", maxWidth: 480, margin: "0 0 24px", lineHeight: 1.5 }}>
            Stiamo già lavorando per sistemarlo. Nel frattempo puoi ricaricare la pagina o tornare alla home.
          </p>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", justifyContent: "center" }}>
            <button
              onClick={async () => {
                // Hard reload: clear caches + unregister SW + force network reload.
                // Solves the "stale PWA bundle" issue users hit after a redeploy.
                try {
                  if ("serviceWorker" in navigator) {
                    const regs = await navigator.serviceWorker.getRegistrations();
                    await Promise.all(regs.map((r) => r.unregister()));
                  }
                  if ("caches" in window) {
                    const keys = await caches.keys();
                    await Promise.all(keys.map((k) => caches.delete(k)));
                  }
                } catch {/* ignore */}
                const url = new URL(window.location.href);
                url.searchParams.set("__r", Date.now().toString(36));
                window.location.replace(url.toString());
              }}
              style={{
                background: "#DC2626", color: "white", border: "none", padding: "12px 24px",
                borderRadius: 999, fontWeight: 700, fontSize: 14, cursor: "pointer",
              }}
              data-testid="error-boundary-reload"
            >
              Ricarica pagina
            </button>
            <button
              onClick={() => { window.location.href = "/"; }}
              style={{
                background: "white", color: "#18181B", border: "1px solid #E4E4E7", padding: "12px 24px",
                borderRadius: 999, fontWeight: 700, fontSize: 14, cursor: "pointer",
              }}
              data-testid="error-boundary-home"
            >
              Torna alla home
            </button>
          </div>
          {process.env.NODE_ENV !== "production" && this.state.error && (
            <pre style={{
              marginTop: 32, padding: 16, background: "#FEF2F2", border: "1px solid #FECACA",
              borderRadius: 8, fontSize: 11, color: "#7F1D1D", maxWidth: 800, overflow: "auto", textAlign: "left",
            }}>
              {String(this.state.error?.message || this.state.error)}
              {"\n\n"}
              {String(this.state.error?.stack || "").split("\n").slice(0, 8).join("\n")}
            </pre>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
